/**
 * The way back after signing in on the start page (/gbp-growth-agent/start).
 *
 * Anand, 26 Sep 26: "keep them all relevant link only from /app page". Every
 * agent WhatsApp and email now links into the app, /app/l/<location>, often
 * with ?card=<id>. An owner who is signed out when they tap it is sent here
 * with ?return_to=<the whole address> (AppLocationShell, useAppAccount,
 * useAgentCaller's handleAuthError). Until this file, the start page read
 * nothing but the campaign tail and the owner was left on the set-up flow,
 * far from the card they tapped.
 *
 * Only an address inside the app is honoured: /app, or anything under /app/.
 * Anything else (another site, //host, a backslash) is ignored, so the start
 * page cannot be used to bounce somebody off Fly.
 *
 * One more guard: the app sends a signed-in person here too when it refuses
 * the page (a location that is not theirs answers 403). Sent straight back,
 * they would be refused again, forever. So a return to the same address
 * within a minute of the last one is not made; the start page carries on
 * with its own flow instead.
 */

const APP_PATH = /^\/app(?:[/?#]|$)/;
const BOUNCE_KEY = 'fly-start-return';
export const BOUNCE_MS = 60_000;

/** The app address to go back to, from the start page's query, or null. */
export function readReturnTo(search: string): string | null {
  const raw = new URLSearchParams(search).get('return_to');
  if (!raw) return null;
  if (!APP_PATH.test(raw) || raw.startsWith('//') || raw.includes('\\')) return null;
  return raw;
}

/**
 * The return_to query part, readable: /app stays /app, not %2Fapp (Shardul, 07 Oct 26: "the link has something
 * gibberish"). A slash is allowed as it is in a query value, and URLSearchParams reads both forms the same;
 * everything else (?, &, =, #, spaces) stays encoded so the value never splits. The sign-in return address on
 * Supabase's allow-list is https://fly-social.com/**, which matches either form.
 */
export function returnToQuery(returnTo: string): string {
  return `return_to=${encodeURIComponent(returnTo).replace(/%2F/gi, '/')}`;
}

/** The start page's own address with the way back kept on it, for every redirect that leaves the page. */
export function withReturnTo(path: string, returnTo: string | null): string {
  if (!returnTo) return path;
  return `${path}${path.includes('?') ? '&' : '?'}${returnToQuery(returnTo)}`;
}

/** True when this same address was returned to a moment ago: going again would loop. */
export function bouncedBack(to: string, now = Date.now()): boolean {
  try {
    const raw = sessionStorage.getItem(BOUNCE_KEY);
    if (!raw) return false;
    const last = JSON.parse(raw) as { to?: unknown; at?: unknown };
    return last.to === to && typeof last.at === 'number' && now - last.at < BOUNCE_MS;
  } catch {
    return false;
  }
}

/** Forgets the last return: a sign out, so the next account gets its own way back. */
export function forgetReturn(): void {
  try {
    sessionStorage.removeItem(BOUNCE_KEY);
  } catch {
    /* nothing to forget */
  }
}

/** Notes the return about to be made, for bouncedBack. */
export function rememberReturn(to: string, now = Date.now()): void {
  try {
    sessionStorage.setItem(BOUNCE_KEY, JSON.stringify({ to, at: now }));
  } catch {
    /* private mode: the loop guard is off, the return still happens */
  }
}
